import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

const STORAGE_KEY = "readRespuestaIdsByFormulario";
const LEGACY_STORAGE_KEY = "readRecibidaIdsByPrueba";

const UnreadRespuestasContext = createContext(null);

export function UnreadRespuestasProvider({ children }) {
  const [unreadByFormulario, setUnreadByFormulario] = useState({});
  /** Por formulario: { [respuestaId]: true } — persiste en localStorage para sobrevivir recargas. */
  const [readRespuestaIdsByFormulario, setReadRespuestaIdsByFormulario] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    // Migración suave desde clave legacy si existe
    try {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      return legacy ? JSON.parse(legacy) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(readRespuestaIdsByFormulario));
    } catch {}
  }, [readRespuestaIdsByFormulario]);

  const totalUnreadRespuestas = useMemo(
    () => Object.values(unreadByFormulario).reduce((a, b) => a + b, 0),
    [unreadByFormulario]
  );

  const setUnreadForFormulario = useCallback((formularioId, count) => {
    setUnreadByFormulario((prev) => ({ ...prev, [formularioId]: count }));
  }, []);

  /** Actualiza en bloque los conteos por formulario (para precarga sin N re-renders). */
  const setBulkUnreadByFormulario = useCallback((updates) => {
    if (!updates || typeof updates !== "object") return;
    setUnreadByFormulario((prev) => ({ ...prev, ...updates }));
  }, []);

  const markRespuestaRead = useCallback((formularioId) => {
    setUnreadByFormulario((prev) => ({
      ...prev,
      [formularioId]: Math.max(0, (prev[formularioId] ?? 0) - 1),
    }));
  }, []);

  /** Marca una respuesta como leída (persiste y decrementa badge). Idempotente. */
  const setRespuestaRead = useCallback((formularioId, respuestaId) => {
    setReadRespuestaIdsByFormulario((prev) => {
      const byFormulario = prev[formularioId] || {};
      if (byFormulario[respuestaId]) return prev;
      return { ...prev, [formularioId]: { ...byFormulario, [respuestaId]: true } };
    });
    setUnreadByFormulario((prev) => ({
      ...prev,
      [formularioId]: Math.max(0, (prev[formularioId] ?? 0) - 1),
    }));
  }, []);

  /** Marca todas las respuestas de los formularios indicados como leídas y pone los badges a 0. data: [{ formularioId, respuestas: [{ id }] }, ...] */
  const markAllRespuestasAsRead = useCallback((data) => {
    if (!Array.isArray(data)) return;
    setReadRespuestaIdsByFormulario((prev) => {
      let next = { ...prev };
      for (const { formularioId, respuestas } of data) {
        if (!formularioId || !Array.isArray(respuestas)) continue;
        const byFormulario = next[formularioId] || {};
        let updated = false;
        for (const r of respuestas) {
          const rid = r?.id ?? r;
          if (rid && !byFormulario[rid]) {
            byFormulario[rid] = true;
            updated = true;
          }
        }
        if (updated) next = { ...next, [formularioId]: { ...byFormulario } };
      }
      return next;
    });
    setUnreadByFormulario((prev) => {
      const next = { ...prev };
      for (const { formularioId } of data) {
        if (formularioId) next[formularioId] = 0;
      }
      return next;
    });
  }, []);

  const isRespuestaRead = useCallback(
    (formularioId, respuestaId) => Boolean(readRespuestaIdsByFormulario[formularioId]?.[respuestaId]),
    [readRespuestaIdsByFormulario]
  );

  return (
    <UnreadRespuestasContext.Provider
      value={{
        unreadByFormulario,
        totalUnreadRespuestas,
        setUnreadForFormulario,
        setBulkUnreadByFormulario,
        markRespuestaRead,
        setRespuestaRead,
        markAllRespuestasAsRead,
        isRespuestaRead,
        readRespuestaIdsByFormulario,
      }}
    >
      {children}
    </UnreadRespuestasContext.Provider>
  );
}

const defaultContext = {
  unreadByFormulario: {},
  totalUnreadRespuestas: 0,
  setUnreadForFormulario: () => {},
  setBulkUnreadByFormulario: () => {},
  markRespuestaRead: () => {},
  setRespuestaRead: () => {},
  markAllRespuestasAsRead: () => {},
  isRespuestaRead: () => false,
  readRespuestaIdsByFormulario: {},
};

export function useUnreadRespuestas() {
  const ctx = useContext(UnreadRespuestasContext);
  return ctx || defaultContext;
}

